import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowDownUp, Check } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Quick sort for a record list.
 *
 * Lives in the shared package because both portals show the same record
 * lists: a client reading their own contacts should get the same control,
 * in the same place, as the person at the agency reading them.
 *
 * Presets rather than a field picker and a direction toggle. "Oldest first" is
 * one decision; `sort=createdAt` plus `order=asc` is two, and the second one is
 * the one people get wrong — every list here defaults to descending, so
 * choosing "created" alone silently gives newest-first and looks broken to
 * somebody hunting for the oldest untouched record.
 *
 * The options a list offers differ because the columns do: a lead has a
 * follow-up date and a value, a company has neither. Ordering by a figure the
 * page computes after fetching — a company's open pipeline, a contact's deal
 * count — is deliberately absent: those are rolled up per page, so sorting by
 * them would order the page rather than the book and break at page two.
 */

export interface SortOption {
  label: string;
  sort: string;
  order: 'asc' | 'desc';
}

/** Shared by every record list. */
const COMMON: SortOption[] = [
  { label: 'Newest first', sort: 'createdAt', order: 'desc' },
  { label: 'Oldest first', sort: 'createdAt', order: 'asc' },
  { label: 'Recently updated', sort: 'updatedAt', order: 'desc' },
  { label: 'Name A–Z', sort: 'name', order: 'asc' },
  { label: 'Name Z–A', sort: 'name', order: 'desc' },
];

export const LEAD_SORT_OPTIONS: SortOption[] = [
  ...COMMON,
  { label: 'Follow-up soonest', sort: 'nextFollowUpAt', order: 'asc' },
  { label: 'Highest value', sort: 'valuePaise', order: 'desc' },
];

export const COMPANY_SORT_OPTIONS = COMMON;
export const PERSON_SORT_OPTIONS = COMMON;

export function SortMenu({
  options,
  value,
  onChange,
}: {
  options: SortOption[];
  value: { sort: string; order: 'asc' | 'desc' };
  onChange: (next: SortOption) => void;
}) {
  const active = options.find((o) => o.sort === value.sort && o.order === value.order);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'flex items-center gap-1.5 rounded-control border border-line bg-white px-2.5 py-1.5',
          'text-[12.5px] font-semibold text-ink-muted transition-colors',
          'hover:border-vir hover:text-ink',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vir',
        )}
      >
        <ArrowDownUp size={13} />
        {/* The current order is named, not just implied by an icon — someone
            arriving at a list they sorted yesterday should not have to open
            the menu to find out what they are looking at. */}
        <span className="hidden sm:inline">{active?.label ?? 'Sort'}</span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-56 overflow-hidden rounded-card border border-line bg-white py-1 shadow-overlay"
        >
          <p className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-ink-soft">
            Sort by
          </p>
          {options.map((option) => {
            const selected = option.sort === value.sort && option.order === value.order;
            return (
              <DropdownMenu.Item
                key={`${option.sort}-${option.order}`}
                onSelect={() => onChange(option)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] outline-none',
                  'data-[highlighted]:bg-surface-sunken',
                  selected ? 'font-semibold text-ink' : 'text-ink-muted',
                )}
              >
                <span className="flex-1">{option.label}</span>
                {selected && <Check size={13} className="shrink-0 text-go-dark" />}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
